import { getDayMetric } from "./utils";
import { isMarkedShift } from "./scheduleWorkingDaysLookup";

/** Група «Дірект»/«Директ» у графіку змін (не «Директор» — це посада). */
export const isDirectScheduleGroup = (group) => /д[іи]рект(?!ор)/.test(String(group || "").toLowerCase());
/** Тільки київський шоурум (без «Шоурум Харків»). */
const isKyivShowroomScheduleGroup = (group) => {
    const g = String(group || "").toLowerCase();
    return g.includes("шоурум") && /ки[їі]в|киев/.test(g);
};

/**
 * Актуальні менеджери для таблиці PR — зі списку учасників графіку (/api/schedule/month):
 * група «Дірект» → Директ, «Шоурум Київ» → офлайн. Архів графіку (колишні) сюди не потрапляє.
 * Продажі зіставляються з KeyCRM за `order.manager.id` = `keycrmManagerId` користувача.
 */
export function pickReportManagersFromSchedule(participants) {
    const direct = [];
    const offline = [];
    for (const p of participants || []) {
        if (p?.historicalScheduleRow) continue;
        const userId = String(p?.userId?._id ?? p?.userId ?? "");
        const label = String(p?.displayName || "").trim();
        if (!userId || !label) continue;
        const keycrmManagerId = p.keycrmManagerId != null ? Number(p.keycrmManagerId) : null;
        const row = { key: userId, userId, label, keycrmManagerId };
        if (isDirectScheduleGroup(p.group)) direct.push(row);
        else if (isKyivShowroomScheduleGroup(p.group)) offline.push(row);
    }
    return { direct, offline };
}

function orderBelongsToManager(order, keycrmManagerId) {
    if (keycrmManagerId == null || !Number.isFinite(keycrmManagerId)) return false;
    return Number(order?.manager?.id) === keycrmManagerId;
}

/** Той самий поділ по `source_id`, що й у PR («Instagram» 1+4, сайт 9, шоурум 10). */
function orderSourceSegment(sourceId) {
    const id = Number(sourceId);
    if ([1, 4].includes(id)) return "instagram";
    if (id === 9) return "site";
    if (id === 10) return "showroom";
    return "other";
}

/** Менеджери Директу, обрані на «Лідах» (Summary.directManager + directManager2). */
export function summaryDirectNames(summaryOrNames) {
    if (Array.isArray(summaryOrNames)) return summaryOrNames.filter(Boolean);
    if (typeof summaryOrNames === "string") return summaryOrNames.trim() ? [summaryOrNames.trim()] : [];
    const a = String(summaryOrNames?.directManager || "").trim();
    const b = String(summaryOrNames?.directManager2 || "").trim();
    return [a, b !== a ? b : ""].filter(Boolean);
}

/**
 * Менеджери Директу на зміні по періодах (днях) — з графіку змін (повний день або 1 / 2 зміна).
 * @returns {string[][]} для кожного періоду — userId менеджерів групи «Дірект», що мають зміну.
 */
export function buildDirectShiftUserIdsByPeriod({ periodDayKeys, scheduleByYm, directManagers }) {
    const directIds = (directManagers || []).map((m) => String(m.userId));
    return (periodDayKeys || []).map((dayKey) => {
        if (!dayKey) return [];
        const ym = dayKey.slice(0, 7);
        const day = String(Number(dayKey.slice(8, 10)));
        const shifts = scheduleByYm?.[ym]?.shifts || {};
        return directIds.filter((id) => isMarkedShift(shifts[id]?.[day]));
    });
}

/**
 * Діалоги: сума метрики «діалоги» (як у таблиці PR) за доби, коли менеджер працював на Директі.
 * Якщо в графіку змін на день відмічено кількох менеджерів Директу — діалоги дня діляться між ними порівну.
 * Якщо в графіку на день нікого з Директу — fallback на менеджера зі сторінки «Ліди» (Summary.directManager).
 * Продажі / ТО: по `orders` з `manager.id` = keycrmManagerId менеджера.
 */
export function computeDirectManagerDialogConversion({
    managers,
    orders,
    ordersByPeriodExtended,
    directManagersByPeriod,
    directShiftUserIdsByPeriod,
    ticketsByPeriod,
    selectedSources,
}) {
    const list = (managers || []).map(({ key, userId, label: directLabel, keycrmManagerId }) => {
        let dialogs = 0;
        let directShifts = 0;
        const n = Math.min(
            ordersByPeriodExtended?.length ?? 0,
            directManagersByPeriod?.length ?? 0,
            ticketsByPeriod?.length ?? 0
        );
        for (let i = 0; i < n; i++) {
            const onShift = directShiftUserIdsByPeriod?.[i] || [];
            let share = 0;
            if (onShift.length > 0) {
                if (onShift.includes(String(userId))) share = 1 / onShift.length;
            } else {
                const names = summaryDirectNames(directManagersByPeriod[i]);
                if (names.includes(directLabel)) share = 1 / names.length;
            }
            if (share === 0) continue;
            directShifts += 1;
            dialogs += getDayMetric(ticketsByPeriod[i] || {}, selectedSources || []) * share;
        }
        dialogs = Math.round(dialogs * 10) / 10;

        let sales = 0;
        let turnover = 0;
        const bySource = {
            instagram: { orders: 0, turnover: 0 },
            site: { orders: 0, turnover: 0 },
            showroom: { orders: 0, turnover: 0 },
            other: { orders: 0, turnover: 0 },
        };

        for (const o of orders || []) {
            if (!orderBelongsToManager(o, keycrmManagerId)) continue;
            sales += 1;
            const gt = Math.floor(Number(o.grand_total) || 0);
            turnover += gt;
            const seg = orderSourceSegment(o.source_id);
            bySource[seg].orders += 1;
            bySource[seg].turnover += gt;
        }

        const percent = dialogs > 0 ? (sales / dialogs) * 100 : null;
        const avgCheck = sales > 0 ? Math.round(turnover / sales) : 0;

        return {
            key,
            userId,
            directLabel,
            dialogs,
            directShifts,
            sales,
            percent,
            turnover,
            avgCheck,
            bySource,
        };
    });
    return list;
}

/** Тільки продажі в звіті: кількість, ТО, середній чек, розбивка по джерелах (без Директу/діалогів). */
export function computeOfflineManagerStats(orders, managers) {
    return (managers || []).map(({ key, userId, label: displayLabel, keycrmManagerId }) => {
        let sales = 0;
        let turnover = 0;
        const bySource = {
            instagram: { orders: 0, turnover: 0 },
            site: { orders: 0, turnover: 0 },
            showroom: { orders: 0, turnover: 0 },
            other: { orders: 0, turnover: 0 },
        };
        for (const o of orders || []) {
            if (!orderBelongsToManager(o, keycrmManagerId)) continue;
            sales += 1;
            const gt = Math.floor(Number(o.grand_total) || 0);
            turnover += gt;
            const seg = orderSourceSegment(o.source_id);
            bySource[seg].orders += 1;
            bySource[seg].turnover += gt;
        }
        const avgCheck = sales > 0 ? Math.round(turnover / sales) : 0;
        return {
            key,
            userId,
            displayLabel,
            sales,
            turnover,
            avgCheck,
            bySource,
        };
    });
}
